import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BookTextIcon, ImageIcon, SendIcon, ServerIcon } from 'lucide-react';
import { EnvironmentSelect } from '@/components/sites/environment-select';
import { useSiteScope } from '@/components/sites/site-scope';
import { CopyField } from '@/components/copy-field';
import { StatusChip } from '@/components/status-chip';
import { Stat } from '@/components/stat';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { messageForError } from '@/lib/api-client';
import { entriesQuery } from '@/lib/api/content';
import { installSnippetQuery } from '@/lib/api/sites';
import type { EntryStatus } from '@/lib/api/types';
import { ENTRY_STATUS_META } from '@/components/content/entry-status-chip';
import { cn, formatDateTime } from '@/lib/utils';

const ORDER: EntryStatus[] = ['published', 'changed', 'draft', 'scheduled', 'unpublished'];

function SiteOverview() {
  const { orgId, siteId } = Route.useParams();
  const { site, environments, environment, environmentId, setEnvironmentId } = useSiteScope();

  const entries = useQuery(
    entriesQuery(orgId, siteId, { environmentId, locale: undefined }),
  );

  const install = useQuery({
    ...installSnippetQuery(orgId, siteId, environmentId ?? ''),
    enabled: environmentId !== null,
  });

  const counts = useMemo(() => {
    const tally: Record<EntryStatus, number> = {
      draft: 0,
      published: 0,
      changed: 0,
      scheduled: 0,
      unpublished: 0,
    };
    for (const entry of entries.data ?? []) tally[entry.status] += 1;
    return tally;
  }, [entries.data]);

  const pendingCount = counts.draft + counts.changed;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-3">
        <EnvironmentSelect
          environments={environments}
          value={environmentId}
          onChange={setEnvironmentId}
        />
        {environment ? (
          <p className="text-micro text-ink-subtle" data-numeric>
            Revision {environment.contentRevision} · last published{' '}
            {environment.lastPublishedAt
              ? formatDateTime(environment.lastPublishedAt)
              : 'never'}
          </p>
        ) : null}
      </div>

      {entries.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
        >
          {messageForError(entries.error, 'Entry counts could not be loaded.')}
        </div>
      ) : null}

      <section
        aria-label="Entries by status"
        className="grid gap-6 rounded-lg border border-hairline bg-elevated px-6 py-6 sm:grid-cols-3 lg:grid-cols-5"
      >
        {ORDER.map((status) => (
          <Stat
            key={status}
            label={ENTRY_STATUS_META[status].label}
            value={counts[status]}
            loading={entries.isLoading}
            tone={status === 'changed' && counts.changed > 0 ? 'warning' : 'default'}
            hint={ENTRY_STATUS_META[status].title}
          />
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <span className="section-label">Installation</span>
            <CardTitle>Connect {environment?.name ?? 'this environment'}</CardTitle>
            <CardDescription>
              Paste this into the page template once. The SDK reads the public key, fetches the
              published content for this environment and swaps it into every tagged element.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {install.isError ? (
              <p role="alert" className="text-data text-danger">
                {messageForError(install.error, 'The install snippet could not be loaded.')}
              </p>
            ) : null}

            {install.data ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <span className="section-label">Snippet</span>
                  <pre className="overflow-x-auto rounded-md border border-hairline bg-sunken px-3 py-2.5 font-mono text-micro leading-relaxed text-ink">
                    <code>{install.data.snippet}</code>
                  </pre>
                </div>

                <CopyField
                  label="Copy the snippet"
                  value={install.data.snippet}
                  truncate
                />

                <div className="flex flex-col gap-1.5">
                  <span className="section-label">Marking content in your HTML</span>
                  <p className="text-data leading-relaxed text-ink-muted">
                    Add{' '}
                    <code className="rounded-xs bg-sunken px-1 font-mono text-micro">
                      data-siteflow-key
                    </code>{' '}
                    to any element an editor should own. The key becomes an entry on this screen the
                    first time the page is scanned or the SDK reports it.
                  </p>
                  <pre className="overflow-x-auto rounded-md border border-hairline bg-sunken px-3 py-2.5 font-mono text-micro leading-relaxed text-ink">
                    <code>{install.data.exampleMarkup}</code>
                  </pre>
                </div>

                <CopyField label="Public key" value={install.data.publicKey} />
              </>
            ) : null}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <span className="section-label">Next</span>
              <CardTitle>
                {pendingCount > 0
                  ? `${pendingCount} ${pendingCount === 1 ? 'entry is' : 'entries are'} waiting to publish`
                  : 'Everything is published'}
              </CardTitle>
              <CardDescription>
                {pendingCount > 0
                  ? `Drafts and changed entries are only visible in the dashboard until you publish them to ${environment?.name ?? 'this environment'}.`
                  : `The draft content matches what ${environment?.name ?? 'this environment'} is serving.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button asChild variant={pendingCount > 0 ? 'primary' : 'secondary'} size="sm">
                <Link to="/app/$orgId/sites/$siteId/publish" params={{ orgId, siteId }}>
                  <SendIcon />
                  Open publish panel
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <span className="section-label">Quick links</span>
              <CardTitle>Work on this site</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-1">
              <QuickLink
                icon={BookTextIcon}
                title="Pages"
                body="Register the pages editors may change and edit their entries."
                to="/app/$orgId/sites/$siteId/pages"
                orgId={orgId}
                siteId={siteId}
              />
              <QuickLink
                icon={BookTextIcon}
                title="Site-wide content"
                body="Navigation, footer and anything shared across every page."
                to="/app/$orgId/sites/$siteId/content"
                orgId={orgId}
                siteId={siteId}
              />
              <QuickLink
                icon={ImageIcon}
                title="Media"
                body="Images and documents referenced by image and file fields."
                to="/app/$orgId/sites/$siteId/media"
                orgId={orgId}
                siteId={siteId}
              />
              <QuickLink
                icon={ServerIcon}
                title="Environments"
                body="Base URLs, allowed origins and public key rotation."
                to="/app/$orgId/sites/$siteId/environments"
                orgId={orgId}
                siteId={siteId}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <span className="section-label">Environments</span>
              <CardTitle>Publication state</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col">
              {environments.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-3 border-b border-hairline py-2 last:border-b-0"
                >
                  <span className="text-data text-ink">{item.name}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-micro text-ink-subtle" data-numeric>
                      rev {item.contentRevision}
                    </span>
                    <StatusChip tone={item.lastPublishedAt ? 'active' : 'neutral'}>
                      {item.lastPublishedAt ? 'Live' : 'Never published'}
                    </StatusChip>
                  </span>
                </div>
              ))}
              <p className="pt-2 text-micro leading-relaxed text-ink-subtle">
                Default locale{' '}
                <code className="font-mono">{site.defaultLocale}</code>
                {site.locales.length > 1 ? ` · ${site.locales.length} locales configured` : ''}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

interface QuickLinkProps {
  icon: typeof BookTextIcon;
  title: string;
  body: string;
  to: string;
  orgId: string;
  siteId: string;
}

function QuickLink({ icon: Icon, title, body, to, orgId, siteId }: QuickLinkProps) {
  return (
    <Link
      to={to}
      params={{ orgId, siteId }}
      className={cn(
        'group flex items-start gap-3 rounded-md border border-transparent px-2 py-2.5',
        'transition-colors hover:border-hairline hover:bg-sunken',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
      )}
    >
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-hairline bg-sunken text-ink-subtle group-hover:text-accent">
        <Icon className="size-3.5" />
      </span>
      <span className="min-w-0">
        <span className="block text-data font-medium text-ink">{title}</span>
        <span className="block text-micro leading-relaxed text-ink-muted">{body}</span>
      </span>
    </Link>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/')({
  component: SiteOverview,
});
